import {API} from 'common/api';
import {SESSION_COOKIE_NAME} from 'config';
import {isbot} from 'isbot';

export type EventAction =
  | 'view'
  | 'click'
  | 'scroll'
  | 'expand'
  | 'search'
  | 'collapse'
  | 'moderation'
  | 'payment'
  | 'open'
  | 'close'
  | 'submit'
  | 'execute'
  | 'start';

type EventComponent =
  | 'header'
  | 'barrier'
  | 'banner'
  | 'page'
  | 'module'
  | 'sticky sidebar'
  | 'modal'
  | 'button'
  | 'reminder'
  | string;

type EventProps = {
  event: string;
  action: EventAction;
  component?: EventComponent;
  content_type?: string;
  content_id?: number;
  content_identifier?: string;
  trigger?: string;
  extra?: Record<string, unknown>;
  // Let the POST survive a page unload — set when the event fires right before a
  // navigation/redirect (OAuth button, post-signup redirect).
  keepalive?: boolean;
};

const isMobile = (userAgent: string): boolean => {
  const toMatch = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ];
  return toMatch.some((re) => re.test(userAgent));
};

const deviceType = (userAgent: string): string => {
  const ua = userAgent.toLowerCase();
  if (ua.includes('iphone') || ua.includes('ipod')) return 'iphone';
  if (ua.includes('ipad')) return 'ipad';
  if (ua.includes('android')) return 'android';
  if (ua.includes('mac os x')) return 'apple';
  if (ua.includes('linux')) return 'linux';
  if (ua.includes('x11')) return 'unix';
  if (ua.includes('windows')) return 'windows';
  return 'unknown';
};

const getCookieValue = (name: string): string =>
  document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() ?? '';

const eventQueue: {body: object; keepalive?: boolean}[] = [];

export const track = async ({
  event,
  action,
  component,
  content_type = '',
  content_id = 0,
  content_identifier = '',
  trigger,
  extra = {},
  keepalive,
}: EventProps): Promise<void> => {
  if (isbot(window.navigator.userAgent)) return;

  const eventBody = {
    event,
    action,
    ...extra,
    component,
    content_type,
    content_id,
    content_identifier,
    trigger,
    user_agent: navigator.userAgent,
    device_type: deviceType(navigator.userAgent),
    raw_url: window.location.href,
    path: window.location.pathname,
    is_mobile: isMobile(navigator.userAgent),
    query_params: JSON.stringify(Object.fromEntries(new URLSearchParams(window.location.search).entries())),
    referer: document.referrer,
  };

  eventQueue.push({body: eventBody, keepalive});
  await flushEvents();
};

const flushEvents = async (): Promise<void> => {
  const attrCookieName = 'attr';
  if (getCookieValue(attrCookieName) || getCookieValue(SESSION_COOKIE_NAME ?? '')) {
    while (eventQueue.length) {
      const item = eventQueue.shift()!;
      await API.post('/v1/track', item.body, {keepalive: item.keepalive});
    }
  }
};

export const pageViewed = (extra?: Record<string, unknown>): Promise<void> =>
  track({
    event: 'page_viewed',
    component: 'page',
    action: 'view',
    extra: extra ? {extra_data: JSON.stringify(extra)} : undefined,
  });
